import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { round } from 'lodash';
import Tippy from '@tippy.js/react';
import {
    IconAssetOffline,
    IconAssetOnline,
    IconHistory,
    IconBatteryHighStatus, IconBatteryHalfStatus, IconBatteryLowStatus, IconInfo, IconWarningType
} from 'svgIcons/MotionPortalIcons';
import { Switch, GeneralDialog, GeneralTable } from 'sharedComponents';
import { getTimeSinceDateString } from 'helpers/dateHelper';
import useGatewayUserCapability from 'sharedHooks/useGatewayUserCapability';
import useUserCapability from 'sharedHooks/useUserCapability';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { apiCallStatus } from 'helpers/constants';
import { assetOnlineStatus } from '../../../constants';
import { ContextMenu } from './ContextMenu';
import useHeaderMenus from '../UseHeaderMenu';
import { EnableDisableDrive } from './EnableDisableDrive';
import { enableDisableDrive } from './EnableDisableDrive/actions';
import { assetTypes } from '../constants';
import { getAssetIcon } from 'helpers/icons';
import { getModel as getDeleteAssetModel } from './DeleteAsset/selectors';
import ConnectionHistoryModal from './ConnectionHistoryModal/ConnectionHistoryModal';
import { getConnectionHistoryAsset, getRssiHistory, clearRssiHistory } from './actions';
import { clearConnectionHistory } from '../../actions';
import { zeroBatteryLevel } from './constants';

const DrivesListRowComponent = ({ gatewayInformation, gatewayConnectedDriveData, assetType, handleShowInfoClick, gatewayID, gatewaySerialNumber, isMoveValid, validationLoadingStatus, isSwitchValid, validateSwitchLoadingStatus, gatewayAssetsList, gatewayAssetsListLoadingStatus, isSwitchEgwToAnotherGateway, switchGatewayId, onResetAssetInfo }) => {
    const { t: translate } = useTranslation();
    const userCapability = useGatewayUserCapability();
    const globalUserCapability = useUserCapability();
    const hasGatewayAdminViewCapability = globalUserCapability?.hasGatewayAdminViewCapability?.() === true;
    const hasGatewayAdminReadViewCapability = globalUserCapability?.hasGatewayAdminReadView?.() === true;
    const isGatewayReadOnlyMode = hasGatewayAdminReadViewCapability && !hasGatewayAdminViewCapability;
    const dispatch = useDispatch();
    const [contextMenuAssetId, setContextMenuAssetId] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [selectedMenuItemId, setSelectedMenuItemId] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [selectedHistoryAsset, setSelectedHistoryAsset] = useState(null);
    const [headerMenus, selectedHeaderMenu] = useHeaderMenus(selectedAsset, assetType, selectedMenuItemId, gatewayID, gatewaySerialNumber);
    const selectedAssetDetails = useSelector(state => state.powertrainTools?.gateway?.selectedAssetDetails);
    const { assetConnectionHistory, loadingStatus: assetConnectionLoadingStatus, error: assetConnectionError, rssiHistoryData, rssiHistoryLoadingStatus } = useSelector(state => state.powertrainTools?.connectionHistoryAsset);
    const deleteAssetModel = useSelector(state => getDeleteAssetModel(state));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [assetId, setAssetId] = useState(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const [showRssiModal, setShowRssiModal] = useState(false);
    const [selectedRssiAsset, setSelectedRssiAsset] = useState(null);

    // Fetch RSSI history data when modal opens
    useEffect(() => {
        if (showRssiModal && selectedRssiAsset?.sensorId !== null && selectedRssiAsset?.sensorId !== undefined) {
            dispatch(getRssiHistory(selectedRssiAsset.sensorId));
        }
    }, [showRssiModal, selectedRssiAsset?.sensorId, dispatch]);

    const hasMotorAssets = gatewayConnectedDriveData?.some(asset => asset?.assetType === assetTypes.MOTOR || asset?.assetType === assetTypes.GENERIC_MACHINE);

    if (gatewayConnectedDriveData?.length === 0) {
        return <div className='empty-drive-list'>{translate('ABB.Powertrain.Frontend.emptyDriveAssets')}</div>;
    }

    const renderAssetName = (connectedAssetItem) => {
        return (
            <div className='drive-name__wrap'>
                <span className='drive-name__text' title={connectedAssetItem?.name}>{connectedAssetItem?.name || ''}</span>
                <span className='drive-name__slno' title={connectedAssetItem?.serialNumber}>{connectedAssetItem?.serialNumber || '-'}</span>
            </div>
        );
    };

    const renderNodeId = (connectedAssetItem) => {
        if (assetType === assetTypes.DRIVE) {
            return (
                <div className='drives-list__cell' id='drives-list-row-asset-node-id'>
                    {connectedAssetItem?.nodeId || '-'}
                </div>
            );
        } else if (assetType === assetTypes.SMART_SENSOR) {
            return null;
        }
    };

    const handleEnableDisableDriveToggle = (assetId, activeValue) => {
        if (activeValue) {
            setIsModalOpen(true);
            setAssetId(assetId);
            setIsDisabled(activeValue);
        } else {
            dispatch(enableDisableDrive(assetId, activeValue, gatewayID));
        }
    };

    const openConnectionHistoryModal = (e, asset) => {
        e.stopPropagation();
        dispatch(getConnectionHistoryAsset(asset?.assetId));
        setSelectedHistoryAsset(asset);
        setShowHistoryModal(true);
    };

    const openRssiModal = (e, asset) => {
        e.stopPropagation();
        setSelectedRssiAsset(asset);
        setShowRssiModal(true);
    };

    const closeRssiModal = () => {
        setShowRssiModal(false);
        setSelectedRssiAsset(null);
        dispatch(clearRssiHistory());
    };

    const handleRssiRetry = () => {
        if (selectedRssiAsset?.sensorId !== null && selectedRssiAsset?.sensorId !== undefined) {
            dispatch(getRssiHistory(selectedRssiAsset.sensorId));
        }
    };

    const getBatteryIcon = (value) => {
        if (value > 50) { return IconBatteryHighStatus; }
        if (value > 20) { return IconBatteryHalfStatus; }
        return IconBatteryLowStatus;
    };

    const formatRssiValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }

        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? `${round(numericValue)} dBm` : '-';
    };

    const supportsSignalAndBattery = (itemAssetType) => {
        return itemAssetType === assetTypes.MOTOR || itemAssetType === assetTypes.GENERIC_MACHINE;
    };

    const getRssiTableData = () => {
        if (Array.isArray(rssiHistoryData) && rssiHistoryData.length > 0) {
            return rssiHistoryData.map((item) => ({
                serialNumber: item?.macAddress || '-',
                rssiValue: item?.rssiAvgValue ?? null
            }));
        }

        // Fallback to current asset RSSI if no history available
        return [{
            serialNumber: selectedRssiAsset?.macAddress || '-',
            rssiValue: selectedRssiAsset?.rssiAverage ?? null
        }];
    };

    const getRssiColumns = () => [
        {
            title: <span className='rssi-table__header-bold'>{translate('ABB.Powertrain.Frontend.GatewayMacAddress')}</span>,
            dataIndex: 'serialNumber',
            key: 'serialNumber',
            render: (_value, record) => <div className='rssi-table__gateway-cell'>
                <div className='rssi-table__gateway-serial'>{record?.serialNumber || '-'}</div>
            </div>
        },
        {
            title: <div className='rssi-table__column-header-flex'>
                <span className='rssi-table__header-bold'>{translate('ABB.Powertrain.Frontend.sensorToGatewayRSSIValue')}</span>
                <Tippy
                    content={translate('ABB.Powertrain.Frontend.SensorToGatewayRSSIValueInfo')}
                    interactive={true}
                    interactiveBorder={10}
                    animation='scale'
                    theme='light-border'
                    trigger='click'
                    placement='top'
                    popperOptions={{
                        modifiers: {
                            preventOverflow: {
                                escapeWithReference: true
                            }
                        }
                    }}>
                    <button onClick={(e) => e.stopPropagation()}>
                        <IconInfo />
                    </button>
                </Tippy>
            </div >,
            dataIndex: 'rssiValue',
            key: 'rssiValue',
            render: (value) => formatRssiValue(value)
        }
    ];
    return (
        <>{gatewayConnectedDriveData?.length > 0 &&
            gatewayConnectedDriveData.map(connectedAssetItem =>
                <button type='button' className={`drives-list__row ${connectedAssetItem?.assetId === selectedAssetDetails?.assetId ? 'active' : ''}`} key={connectedAssetItem?.assetId} onClick={() => handleShowInfoClick(connectedAssetItem)}>
                    <div className={`drives-list__cell drives-list__cell--sticky ${connectedAssetItem?.assetId === selectedAssetDetails?.assetId ? 'active' : ''}`} id='drives-list-row-asset-name'>
                        <div className='drive-name'>
                            <div className='drive-name__icon'>
                                {getAssetIcon(connectedAssetItem?.assetType, { width: 16, height: 16 })}
                            </div>
                            {renderAssetName(connectedAssetItem)}
                        </div>
                    </div>
                    <div className='drives-list__cell' id='drives-list-row-asset-status'>
                        <div className='cell-wrap'>
                            <div className='cell-wrap-connection-status'>
                                {connectedAssetItem?.telemetryConnectionStatus?.status === assetOnlineStatus.ONLINE ?
                                    <IconAssetOnline />
                                    :
                                    <IconAssetOffline />
                                }
                                <span className='cell-wrap__label'>
                                    {connectedAssetItem?.telemetryConnectionStatus?.status ===
                                        assetOnlineStatus.ONLINE
                                        ? translate(
                                            'ABB.Powertrain.Frontend.gatewayStatusOnlineLabel'
                                        )
                                        : translate(
                                            'ABB.Powertrain.Frontend.gatewayStatusOfflinLabel'
                                        )}
                                </span>
                                <span className='asset-status-history' id={`asset-status-history-${connectedAssetItem?.assetId}`}>
                                    <button className='asset-status-history-button' onClick={(e) => openConnectionHistoryModal(e, connectedAssetItem)}>
                                        <IconHistory />
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className='drives-list__cell' id='drives-list-row-asset-timestamp'>
                        {getTimeSinceDateString(connectedAssetItem?.telemetryConnectionStatus?.transitionTimestamp)}
                    </div>
                    {hasMotorAssets && <>
                        <div className='drives-list__cell' id='drives-list-row-asset-signal-strength'>
                            <div className='cell-wrap-connection-status'>
                                {supportsSignalAndBattery(connectedAssetItem?.assetType) ?
                                    <>
                                        <span className='rssi-value__text'>{formatRssiValue(connectedAssetItem?.rssiAverage)}</span>
                                        {connectedAssetItem?.rssiAverage !== null && connectedAssetItem?.rssiAverage !== undefined && connectedAssetItem?.rssiAverage !== '' &&
                                            <span className='asset-status-history' id={`asset-rssi-history-${connectedAssetItem?.assetId}`}>
                                                <button className='icon-action-button' onClick={(e) => openRssiModal(e, connectedAssetItem)}>
                                                    <IconHistory />
                                                </button>
                                            </span>
                                        }
                                    </>
                                    :
                                    <span className='rssi-value__placeholder'>-</span>
                                }
                            </div>
                        </div>
                        <div className='drives-list__cell' id='drives-list-row-asset-battery-status'>
                            {(() => {
                                if (!supportsSignalAndBattery(connectedAssetItem?.assetType)) {
                                    return <div className='cell-wrap-connection-status'><span className='cell-wrap__label'>-</span></div>;
                                }

                                const batteryLevel = connectedAssetItem?.batteryLevel;
                                const hasBatteryLevel = batteryLevel || batteryLevel === zeroBatteryLevel; // Check for both null/undefined and 0
                                const batteryLevelDisplay = hasBatteryLevel
                                    ? `${round(batteryLevel)}%`
                                    : '-';
                                const BatteryIcon = hasBatteryLevel
                                    ? getBatteryIcon(batteryLevel)
                                    : IconBatteryLowStatus;
                                const isAssetOnline = connectedAssetItem?.telemetryConnectionStatus?.status === assetOnlineStatus.ONLINE;
                                return (
                                    <div className='cell-wrap-connection-status'>
                                        <div className='cell-wrap'>
                                            {hasBatteryLevel && <BatteryIcon />}
                                            <span className='cell-wrap__label'>{batteryLevelDisplay}</span>
                                        </div>
                                        {hasBatteryLevel && !isAssetOnline &&
                                            <Tippy
                                                content={translate('ABB.Powertrain.Frontend.gatewaySmartSensorBatteryLastKnownValue')}
                                                interactive={true}
                                                animation='scale'
                                                theme='light-border'
                                                trigger='click'
                                                placement='top'>
                                                <button className='icon-action-button' type='button' onClick={(e) => e.stopPropagation()}>
                                                    <IconWarningType />
                                                </button>
                                            </Tippy>
                                        }
                                    </div>
                                );
                            })()}
                        </div>
                    </>}
                    {renderNodeId(connectedAssetItem)}
                    <div className={`drives-list__cell drives-list__cell--sticky-right ${!userCapability.hasDriveAdminCapability() ? 'drives-list__cell--width' : ''} ${connectedAssetItem?.assetId === selectedAssetDetails?.assetId ? 'active' : ''}`} id='drives-list-row-asset-action'>
                        <div className={`d-flex justify-content-${userCapability.hasDriveAdminCapability() ? 'around' : 'center'} align-items-center w-100`}>
                            {userCapability.hasDriveAdminCapability() && <button
                                className='drives-list__active-asset'
                                onClick={e => e.stopPropagation()}
                                type='button'>
                                <Switch
                                    onChange={() => handleEnableDisableDriveToggle(connectedAssetItem?.assetId, connectedAssetItem?.active)}
                                    checked={connectedAssetItem?.active}
                                />
                                <div className='event-list-control-label'>
                                    {connectedAssetItem?.active ? translate('ABB.Powertrain.Frontend.drivesDataActiveLabel') : translate('ABB.Powertrain.Frontend.drivesDataInactiveLabel')}
                                </div>
                            </button>}
                            {!isGatewayReadOnlyMode && <button
                                className='drives-context-menu-wrapper'
                                onClick={e => e.stopPropagation()}
                                type='button'>
                                <ContextMenu
                                    id={connectedAssetItem?.name}
                                    options={headerMenus.map(menu => ({
                                        id: `contextMenuItem_${menu.id}`,
                                        title: menu.title,
                                        onOptionSelect: () => {
                                            setSelectedAsset(connectedAssetItem);
                                            setSelectedMenuItemId(menu.id);
                                            setContextMenuAssetId(null);
                                        },
                                        disabled: menu.disabled || false
                                    }))}
                                    open={contextMenuAssetId === connectedAssetItem.assetId}
                                    onToggle={isOpen => setContextMenuAssetId(isOpen ? connectedAssetItem.assetId : null)}
                                    onClose={() => setContextMenuAssetId(null)}
                                />
                            </button>}
                        </div>
                    </div>

                </button>
            )}
            {!isGatewayReadOnlyMode && selectedHeaderMenu?.modalContentComponent &&
                <selectedHeaderMenu.modalContentComponent
                    asset={selectedAsset}
                    gatewayInformation={gatewayInformation}
                    show={true}
                    close={() => setSelectedMenuItemId(null)}
                    headerMenus={headerMenus}
                    contextMenuAssetId={contextMenuAssetId}
                    gatewayID={gatewayID}
                    gatewaySerialNumber={gatewaySerialNumber}
                    isSwitchValid={isSwitchValid}
                    validateSwitchLoadingStatus={validateSwitchLoadingStatus}
                    gatewayAssetsList={gatewayAssetsList}
                    gatewayAssetsListLoadingStatus={gatewayAssetsListLoadingStatus}
                    isSwitchEgwToAnotherGateway={isSwitchEgwToAnotherGateway}
                    isMoveValid={isMoveValid}
                    validationLoadingStatus={validationLoadingStatus}
                    switchGatewayId={switchGatewayId}
                    onResetAssetInfo={onResetAssetInfo}
                    model={deleteAssetModel}
                />
            }
            {isModalOpen && <EnableDisableDrive
                assetId={assetId}
                show={isModalOpen}
                close={() => setIsModalOpen(false)}
                isDisabled={isDisabled}
                gatewayID={gatewayID}
            />}
            {
                showHistoryModal && <ConnectionHistoryModal
                    isOpen={showHistoryModal}
                    onClose={() => { setShowHistoryModal(false); dispatch(clearConnectionHistory()); }}
                    historyData={assetConnectionHistory}
                    loadingStatus={assetConnectionLoadingStatus}
                    error={assetConnectionError}
                    onRetry={() => dispatch(getConnectionHistoryAsset(selectedHistoryAsset?.assetId))}
                />
            }
            {
                showRssiModal && selectedRssiAsset &&
                <GeneralDialog
                    show={showRssiModal}
                    close={closeRssiModal}
                    modalClassName='rssi-modal-dialog'
                    title={<div className='rssi-modal__title'>{translate('ABB.Powertrain.Frontend.gatewaySignalStrengthHistoryDialogTitle')}</div>}
                    closeButton={true}
                    leftFooterButtonProps={rssiHistoryLoadingStatus === apiCallStatus.ERROR
                        ? {
                            text: translate('ABB.Powertrain.Frontend.erroBoundaryRetryLabel'),
                            onClick: handleRssiRetry,
                            keepOpenOnClick: true
                        }
                        : null}
                    acceptButtonProps={{
                        text: translate('ABB.Powertrain.Frontend.closeBtnLbl'),
                        onClick: closeRssiModal
                    }}
                >
                    <div className='rssi-modal__content'>
                        <LoadingStatusWrapper loadingStatus={rssiHistoryLoadingStatus}>
                            {rssiHistoryLoadingStatus === apiCallStatus.ERROR
                                ? <div className='rssi-modal__error-wrapper'>
                                    <div className='rssi-modal__error-content'>
                                        <p className='rssi-modal__error-message'>{translate('ABB.Powertrain.Frontend.gatewayRSSIModelErrorMessage')}</p>
                                        <p className='rssi-modal__error-message-generic'>{translate('ABB.Powertrain.Frontend.gatewayRSSIModelGenericErrorMessage')}</p>
                                    </div>
                                </div>
                                : <>
                                    <div className='rssi-modal__sensor-section'>
                                        <div className='rssi-modal__sensor-label'>
                                            {translate('ABB.Powertrain.Frontend.sensorIdentifier')}
                                        </div>
                                        <div className='rssi-modal__sensor-id-box'>
                                            {rssiHistoryData?.[0]?.sensorIdentifier ?? selectedRssiAsset?.sensorId ?? '-'}
                                        </div>
                                    </div>
                                    <GeneralTable
                                        className='msr-details-table'
                                        columns={getRssiColumns()}
                                        data={getRssiTableData()}
                                        paginationProps={{ pageSize: 10, hideOnSinglePage: true }}
                                    />
                                </>
                            }
                        </LoadingStatusWrapper>
                    </div>
                </GeneralDialog>
            }
        </>
    );
};

DrivesListRowComponent.propTypes = {
    gatewayInformation: PropTypes.object,
    gatewayConnectedDriveData: PropTypes.array,
    handleShowInfoClick: PropTypes.func,
    assetType: PropTypes.string,
    gatewayID: PropTypes.number,
    gatewaySerialNumber: PropTypes.string,
    isMoveValid: PropTypes.bool,
    validationLoadingStatus: PropTypes.string,
    onResetAssetInfo: PropTypes.func
};

export default DrivesListRowComponent;
